import styled, { keyframes, css } from "../../__compat__/styled-components/main.entry.mjs.mjs";
import { injectSpacing } from "../../api/spacing/injectSpacing.mjs.mjs";
import { injectTypography } from "../../utils/injectTypography.mjs.mjs";
/** Circumfence in pixels based on --size of the circle */
const circumfence = `calc(${Math.PI} * (var(--size) - var(--stroke-width)))`;
/** Convert degrees of an arc to dash lengths in pixels based on --size of the circle */
const dashLengthFromAngle = (deg) => `calc(${deg} / 360 * ${circumfence})`;
/** Layer one of the continuous spinning animation - pure rotation */
const continuousSpinningKeyframes = keyframes `
  from {
    transform: rotate(-90deg)
  }
  to {
    transform: rotate(270deg);
  }
`;
/** Layer two of the continuous spinning animation - SVG strokes */
const continuousCircularLoadingKeyframes = keyframes `
  0% {
      stroke-dasharray: ${[0, 360].map(dashLengthFromAngle).join(',')};
      stroke-dashoffset: ${dashLengthFromAngle(0)};

      animation-timing-function: ease-in;
  }
  20% {
      stroke-dasharray: ${[200, 360].map(dashLengthFromAngle).join(',')};
      stroke-dashoffset: ${dashLengthFromAngle(-40)};
  }
  100% {
      stroke-dasharray: ${[200, 360].map(dashLengthFromAngle).join(',')};
      stroke-dashoffset: ${dashLengthFromAngle(-360)};
  }
`;
const continuousCircularLoadingAnimation = css `
  animation: ${continuousCircularLoadingKeyframes} 1.5s
    cubic-bezier(0.3, 0.95, 0.65, 0.9) infinite both;
`;
// Handle determinate percentage values (as opposed to the continual spinner)
const circularPercentValue = css `
  stroke-dasharray: calc(var(--progress) * ${circumfence}),
    ${dashLengthFromAngle(360)};

  transform: rotate(-90deg);

  transition: stroke-dasharray 0.1s var(${(props) => props.theme.easing});
`;
/** Continuous horizontal bar animation */
const continuousHorizontalLoadingKeyframes = keyframes `
  0% {
    transform: translateX(0%) scaleX(0.0);
  }
  50% {
    transform: translateX(20%) scaleX(0.5);

    animation-timing-function: cubic-bezier(0,0,0,1);
  }
  100% {
    transform: translateX(100%) scaleX(0.5);
  }
`;
const continuousHorizontalLoadingAnimation = css `
  animation: ${continuousHorizontalLoadingKeyframes} 1s
    cubic-bezier(0.85, 0, 1, 1) infinite;
`;
/** Handle determinate percentage values (as opposed to the continual spinner) */
const horizontalPercentValue = css `
  transform: scaleX(var(--progress));

  transition: transform ease-in-out 0.1s;
`;
export const circularLoaderSizes = {
    continuous: {
        small: {
            diameter: 24,
            stroke: 2,
        },
        large: {
            diameter: 48,
            stroke: 3,
        },
    },
    determinate: {
        small: {
            diameter: 48,
            stroke: 3,
        },
        large: {
            diameter: 96,
            stroke: 4,
        },
    },
};
export const StyledRoot = styled.div.withConfig({ displayName: "auir-611444a-StyledRoot", componentId: "auir-611444a-nuzvvy" }) `
  display: ${(props) => props.variant === 'circular' ? 'inline-flex' : 'flex'};
  flex-direction: column;
  position: relative;
  color: var(
    ${(props) => props.inverted
    ? props.theme.colors.ui.inverted
    : props.theme.colors.ui.highlight.secondary}
  );

  ${injectSpacing('margin')}
`;
export const StyledLoaderContainer = styled.div.withConfig({ displayName: "auir-611444a-StyledLoaderContainer", componentId: "auir-611444a-q6vtm3" }) `
  --size: ${(props) => circularLoaderSizes[props.progressType][props.size].diameter}px;
  --stroke-width: ${(props) => circularLoaderSizes[props.progressType][props.size].stroke}px;
  --train-color: var(
    ${(props) => props.monochrome
    ? props.inverted
        ? props.theme.colors.ui.inverted
        : props.theme.colors.ui.highlight.secondary
    : props.theme.colors.ui.highlight.primary}
  );
  --track-color: var(
    ${(props) => props.inverted
    ? props.theme.colors.ui.secondary
    : props.theme.colors.ui.divider}
  );
  --progress: ${(props) => props.progress};

  position: relative;
  align-self: center;

  display: ${(props) => props.variant === 'linear' ? ' block' : 'inline-block'};

  width: ${(props) => props.variant === 'linear' && ' 100%'};
`;
// Circular loader styles
export const StyledCircularLoader = styled.svg.withConfig({ displayName: "auir-611444a-StyledCircularLoader", componentId: "auir-611444a-1yjmvbt" }) `
  display: block;
  width: var(--size);
  height: var(--size);

  transform-origin: 50% 50%;

  animation: ${(props) => props.progressType === 'continuous' &&
    css `
      ${continuousSpinningKeyframes} 1.5s cubic-bezier(0.4, 0, 0.3, 1) infinite
    `};
`;
export const StyledCircularTrain = styled.circle.withConfig({ displayName: "auir-611444a-StyledCircularTrain", componentId: "auir-611444a-16ws8xb" }) `
  transform-origin: 50% 50%;

  fill: none;

  stroke: var(--train-color);
  stroke-width: calc(var(--stroke-width));

  ${(props) => props.progressType === 'continuous'
    ? continuousCircularLoadingAnimation
    : circularPercentValue}
`;
export const StyledCircularTrack = styled.circle.withConfig({ displayName: "auir-611444a-StyledCircularTrack", componentId: "auir-611444a-480zz7" }) `
  fill: none;

  stroke: var(--track-color);
  stroke-width: var(--stroke-width);
`;
export const StyledValue = styled.div.withConfig({ displayName: "auir-611444a-StyledValue", componentId: "auir-611444a-r3foq4" }) `
  position: absolute;
  top: 50%;
  left: 50%;

  max-width: calc(var(--size) - var(--stroke-width) * 2 - 1ch);
  overflow: hidden;

  ${(props) => injectTypography(props, props.size === 'large' ? 'copy1' : 'copy2')}
  text-overflow: ellipsis;

  transform: translate(-50%, -50%);
`;
export const StyledLabel = styled.div.withConfig({ displayName: "auir-611444a-StyledLabel", componentId: "auir-611444a-1jiwp70" }) `
  padding-top: 100px;
  align-self: ${(props) => props.variant === 'circular' ? 'center' : 'flex-start'};
  padding-block-start: var(${(props) => props.theme.responsive.spacing.xs});
  text-align: start;

  ${(props) => injectTypography(props, 'copy2')}
`;
// Horizontal loader styles
export const StyledProgressBar = styled.div.withConfig({ displayName: "auir-611444a-StyledProgressBar", componentId: "auir-611444a-clmixc" }) `
  position: relative;

  width: 100%;
  height: 4px;
  overflow: hidden;

  background-color: var(--track-color);
`;
export const StyledProgressBarTrain = styled.div.withConfig({ displayName: "auir-611444a-StyledProgressBarTrain", componentId: "auir-611444a-1foc99h" }) `
  position: absolute;
  top: 0%;

  width: 100%;
  height: 100%;

  background-color: var(--train-color);

  transform-origin: 0% 0%;

  ${(props) => props.progressType === 'continuous'
    ? continuousHorizontalLoadingAnimation
    : horizontalPercentValue}
`;
//# sourceMappingURL=Loader.styles.mjs.mjs.map